/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { Box, Heading, Icon, Text } from '@landtrustinc/design-system';

import type { BuckChatWelcomeContent } from '../../types';
import StarterPrompts from './components/StarterPrompts';

type HomepageEmptyStateProps = {
  content?: BuckChatWelcomeContent;
  disabled?: boolean;
  onPromptClick?: (prompt: string) => void;
  starterPromptsTitle?: string;
};

const DEFAULT_STARTER_PROMPTS_TITLE = 'Try out some of the things I can do for you.';

const HomepageEmptyState = ({
  content,
  disabled = false,
  onPromptClick,
  starterPromptsTitle = DEFAULT_STARTER_PROMPTS_TITLE,
}: HomepageEmptyStateProps) => {
  const resolvedContent: BuckChatWelcomeContent = {
    heading: "Hi, I'm Buck,",
    subheading: 'Your trip planning assistant.',
    introBody:
      'Ready to help you find the perfect hunting trip. Just tell me what you’re interested in.',
    starterPrompts: [
      {
        label: 'What are some of the best hunting properties near me?',
        value: 'What are some of the best hunting properties near me?',
      },
      {
        label: "I'm interested in a mule deer hunt, what tags do I need?",
        value: "I'm interested in a mule deer hunt, what tags do I need?",
      },
      {
        label: 'Which properties have been hunted recently?',
        value: 'Which properties have been hunted recently?',
      },
    ],
    widgetTitle: '',
    ...content,
  };

  return (
    <Box width="100%">
      <Box display="flex" flexDirection="column" gap="60px" alignItems="center">
        <Box display="flex" flexDirection="column" gap="var(--spacing-5)" alignItems="center">
          <Box width="100%" display="flex" justifyContent="center">
            <Box display="block" flex="0 0 auto">
              <Icon
                variant="BuckChat"
                size="xxxlarge"
                setAutoHeight
                fill="var(--surface-action)"
                css={css`
                  width: 82px;
                  height: 79px;
                  color: var(--surface-action);
                `}
              />
            </Box>
          </Box>

          <Box display="flex" flexDirection="column" gap="var(--spacing-5)" alignItems="center">
            <Box display="flex" flexDirection="column" textAlign="center">
              <Heading size="xs" fontWeight="bold" color="var(--text-primary)">
                {resolvedContent.heading}
              </Heading>
              {resolvedContent.subheading && (
                <Heading size="xs" color="var(--text-primary)">
                  {resolvedContent.subheading}
                </Heading>
              )}
            </Box>

            {resolvedContent.introBody && (
              <Box maxWidth="325px" textAlign="center">
                {typeof resolvedContent.introBody === 'string' ? (
                  <Text size="md" color="var(--text-primary)" textAlign="center">
                    {resolvedContent.introBody}
                  </Text>
                ) : (
                  resolvedContent.introBody
                )}
              </Box>
            )}
          </Box>
        </Box>

        {resolvedContent.starterPrompts && resolvedContent.starterPrompts.length > 0 && (
          <StarterPrompts
            disabled={disabled}
            onPromptClick={onPromptClick}
            prompts={resolvedContent.starterPrompts}
            title={starterPromptsTitle}
          />
        )}
      </Box>
    </Box>
  );
};

export default HomepageEmptyState;
